// ============================================================================
// STATISTIQUES › VENTES — un écran, quatre onglets (Résumé · Reporting ventes · Reporting articles · Objectifs) qui
// partagent leur barre de réglages. Réf. maquette du back-office (« STATISTIQUES · VENTES ») et
// docs/CADRAGE-Statistiques-Ventes.md.
//
// ⚠️ LES ONGLETS SONT DES VUES DU MÊME ÉCRAN, pas des routes : la période, la comparaison et les filtres choisis
//    restent quand on passe de l'un à l'autre. L'onglet ouvert vit dans l'adresse (`?onglet=`), pour qu'un
//    rechargement ou un lien partagé rouvre le même.
// ⚠️ L'ONGLET OBJECTIFS A SA PROPRE PÉRIODE : un objectif court jusqu'au bout de son mois, ses jours à venir portent une
//    cible — et il n'a ni comparaison ni filtre (l'objectif est celui du magasin entier).
// ⚠️ CHAQUE ONGLET LIT SES PROPRES CHIFFRES, et seulement quand il est ouvert : le Résumé et le Reporting ne
//    demandent pas la même chose au serveur, et un onglet fermé ne charge rien.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState, Icon, Tabs, Trans, showToast } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import { api, type ArticleReportAxis, type ArticleReportView, type ObjectiveTrackingView, type SalesReportView, type SalesSummaryAxis, type SalesSummaryFilterKey, type SalesSummaryView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { useStoreScope } from '../store-context';
import { useAncreDuJour } from '../periode';
import { exporterCsv } from './export-csv';
import { ARTICLE_LEVEL_DIMS, FILTER_DIMS, StatsBar, StatsLinesRow, StatsPeriodCard, defaultSettings, isoOf, rangeOf, type StatsSettings, type StatsWindow } from './stats-bar';
import { StatsObjectives, defaultObjectiveWindow, exportObjectives, useObjectiveColumns } from './stats-objectifs';
import {
  ARTICLE_DEFAULT_AXES, ARTICLE_DEFAULT_SORT, StatsArticlesTable, articleLegend, articleScope, exportArticles, useArticleColumns,
} from './stats-articles';
import { StatsColumnsButton, type StatsSort } from './stats-grid';
import { csvQty, csvRate, shownValue } from './stats-format';
import {
  REPORT_DEFAULT_SORT, ReportColumnsButton, ReportDisplayToggle, StatsReportTable, exportReport, needsOrders, needsStock, reportLegend, useReportColumns,
  type ReportDisplay, type ReportSort,
} from './stats-reporting';
import { AXES, StatsResume, StatsResumePlaceholder } from './stats-resume';
import { StatsViewsButton, describeView, keptColumns, keptSort, type StatsViewAdapter } from './stats-views';
import { fieldLabel } from '../vocabulary';

type TabId = 'resume' | 'reporting' | 'articles' | 'objectifs';
const TABS: { id: TabId; label: Translatable; icon: IconName }[] = [
  { id: 'resume', label: key('Résumé'), icon: 'dashboard' },
  { id: 'reporting', label: key('Reporting ventes'), icon: 'table' },
  { id: 'articles', label: key('Reporting articles'), icon: 'tag' },
  { id: 'objectifs', label: key('Objectifs'), icon: 'target' },
];

type Phase = { phase: 'idle' } | { phase: 'loading' } | { phase: 'ready' } | { phase: 'failed'; message: string };

/** `AAAA-MM-JJ` → `AAAAMMJJ`, la forme que le serveur lit. */
const ymd = (iso: string): string => iso.replace(/-/g, '');

export function StatsVentes() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((x) => x.id === params.get('onglet'))?.id ?? 'resume') as TabId;
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const ancre = useAncreDuJour();
  const today = isoOf(ancre);
  const [settings, setSettings] = useState<StatsSettings>(() => defaultSettings(ancre));
  const [axis, setAxis] = useState<SalesSummaryAxis>('fam');
  const [view, setView] = useState<SalesSummaryView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const shown = useRef(false);
  // Reporting ventes : ses niveaux, son affichage et son tri survivent au passage d'un onglet à l'autre.
  const [axes, setAxes] = useState<SalesSummaryFilterKey[]>(['sup']);
  const [display, setDisplay] = useState<ReportDisplay>('a');
  const [sort, setSort] = useState<ReportSort>(REPORT_DEFAULT_SORT);
  const [report, setReport] = useState<SalesReportView | null>(null);
  const [reportStatus, setReportStatus] = useState<Phase>({ phase: 'idle' });
  const reportShown = useRef(false);
  const summaryBusy = status.phase === 'loading';
  const reportBusy = reportStatus.phase === 'loading';
  const columns = useReportColumns(tab === 'reporting', (message) => showToast(t('Réglage non mémorisé : {v}', { v: message }), 'err'));
  // Reporting articles : sa fenêtre Réceptions, ses niveaux et son tri survivent eux aussi au passage d'un onglet à l'autre.
  const [receptions, setReceptions] = useState<StatsWindow>(() => defaultReceptions(ancre));
  const [artAxes, setArtAxes] = useState<ArticleReportAxis[]>(ARTICLE_DEFAULT_AXES);
  const [artSort, setArtSort] = useState<StatsSort>(ARTICLE_DEFAULT_SORT);
  const [artReport, setArtReport] = useState<ArticleReportView | null>(null);
  const [artStatus, setArtStatus] = useState<Phase>({ phase: 'idle' });
  const artShown = useRef(false);
  const artBusy = artStatus.phase === 'loading';
  const artColumns = useArticleColumns(tab === 'articles', (message) => showToast(t('Réglage non mémorisé : {v}', { v: message }), 'err'));
  // Objectifs : sa période (le mois entier par défaut) et son tableau survivent au passage d'un onglet à l'autre.
  const [objWindow, setObjWindow] = useState<StatsWindow>(() => defaultObjectiveWindow(ancre));
  const [objView, setObjView] = useState<ObjectiveTrackingView | null>(null);
  const [objStatus, setObjStatus] = useState<Phase>({ phase: 'idle' });
  const objBusy = objStatus.phase === 'loading';
  const objColumns = useObjectiveColumns(tab === 'objectifs', (message) => showToast(t('Réglage non mémorisé : {v}', { v: message }), 'err'));
  const objRange = rangeOf(objWindow);

  /**
   * LES RÉGLAGES DE LA BARRE. Choisir une comparaison montre l'ÉCART, l'arrêter revient à la période (maquette) : c'est
   * la comparaison que l'on vient de demander qu'on veut lire.
   */
  const changeSettings = (next: StatsSettings) => {
    if (settings.b === null && next.b !== null) setDisplay('d');
    if (next.b === null) setDisplay('a');
    setSettings(next);
  };
  /** Redessiner les niveaux rend l'ordre de la ventilation : un tri manuel ne décrit plus le tableau affiché. */
  const changeAxes = (next: SalesSummaryFilterKey[]) => { setAxes(next); setSort(REPORT_DEFAULT_SORT); };

  const a = rangeOf(settings.a);
  const b = settings.b ? rangeOf(settings.b) : null;
  const request = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    axis, q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, axis, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ UN BLOC N'EST DEMANDÉ QUE SI L'UNE DE SES COLONNES EST AFFICHÉE : le rejeu du grand livre est la lecture la plus lourde.
  const withStock = needsStock(columns.visible);
  const withOrders = needsOrders(columns.visible);
  const reportRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    axes, q: settings.q.trim(), filters: settings.filters, ...(withStock ? { stock: true } : {}), ...(withOrders ? { orders: true } : {}),
  }), [a.from, a.to, b?.from, b?.to, axes, settings.q, settings.filters, withStock, withOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Redessiner les niveaux rend l'ordre de la ventilation, comme au Reporting ventes. */
  const changeArtAxes = (next: ArticleReportAxis[]) => { setArtAxes(next); setArtSort(ARTICLE_DEFAULT_SORT); };
  const r = rangeOf(receptions);
  // ⚠️ AUCUNE COMPARAISON N'EST ENVOYÉE : celle choisie au Reporting ventes n'a pas de sens face à deux fenêtres.
  const articleRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), recFrom: ymd(r.from), recTo: ymd(r.to),
    axes: artAxes, q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, r.from, r.to, artAxes, settings.q, settings.filters]);

  useEffect(() => {
    if (!gln || tab !== 'articles' || !articleRequest.from || !articleRequest.recFrom) { setArtStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ LE VOILE TOMBE TOUT DE SUITE, L'APPEL ATTEND LA FIN DE LA FRAPPE : les chiffres d'avant ne décrivent plus les
    //    réglages affichés dès la première touche, et l'export doit être fermé pendant ce temps aussi.
    setArtStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.articleReport(articleRequest)
        .then((v) => { if (!alive) return; artShown.current = true; setArtReport(v); setArtStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Le tableau précédent est JETÉ : garder les chiffres de fenêtres qu'on vient de quitter mentirait.
          setArtReport(null);
          setArtStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, artShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, articleRequest]);

  useEffect(() => {
    if (!gln || tab !== 'objectifs' || !objRange.from || !objRange.to) { setObjStatus({ phase: 'idle' }); return; }
    let alive = true;
    setObjStatus({ phase: 'loading' });
    api.objectiveTracking({ from: ymd(objRange.from), to: ymd(objRange.to) })
      .then((v) => { if (!alive) return; setObjView(v); setObjStatus({ phase: 'ready' }); })
      .catch((e: unknown) => {
        if (!alive) return;
        // ⚠️ Le suivi précédent est JETÉ : garder les chiffres d'une période qu'on vient de quitter mentirait.
        setObjView(null);
        setObjStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { alive = false; };
  }, [gln, tab, objRange.from, objRange.to]);

  useEffect(() => {
    if (!gln || tab !== 'reporting' || !reportRequest.from || !reportRequest.to) { setReportStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Le voile tombe tout de suite, l'appel attend la fin de la frappe (même règle que les articles).
    setReportStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.salesReport(reportRequest)
        .then((v) => { if (!alive) return; reportShown.current = true; setReport(v); setReportStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Le tableau précédent est JETÉ : garder les chiffres d'une période qu'on vient de quitter mentirait.
          setReport(null);
          setReportStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, reportShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, reportRequest]);

  useEffect(() => {
    // ⚠️ AUCUNE REQUÊTE SANS MAGASIN NI HORS DU RÉSUMÉ : chaque onglet lit ses propres chiffres.
    if (!gln || tab !== 'resume' || !request.from || !request.to) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ LA RECHERCHE ATTEND QU'ON AIT FINI DE TAPER : une requête par lettre recalculerait toute la période. Le voile,
    //    lui, tombe tout de suite (même règle que les articles).
    setStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.salesSummary(request)
        .then((v) => { if (!alive) return; shown.current = true; setView(v); setStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ La donnée précédente est JETÉE : garder les chiffres d'une période qu'on vient de quitter mentirait.
          setView(null);
          setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, shown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, request]);

  const pick = (id: TabId) => {
    const next = new URLSearchParams(params);
    if (id === 'resume') next.delete('onglet'); else next.set('onglet', id);
    setParams(next, { replace: true });
  };

  // ─── Les vues enregistrées ─────────────────────────────────────────────────────────────────────
  /**
   * ⚠️ LA BARRE EST PARTAGÉE PAR LES ONGLETS : la période et les filtres d'une vue valent pour tous, comme s'ils avaient été
   *    choisis à la main. Le Reporting articles ne compare pas : sa vue laisse la comparaison des autres en place.
   * ⚠️ LES COLONNES D'UNE VUE DEVIENNENT CELLES DU COMPTE : elles sont mémorisées sur le profil, comme un réglage à la main.
   */
  const reportViews: StatsViewAdapter = {
    snapshot: () => ({ a: settings.a, b: settings.b, q: settings.q, filters: settings.filters, axes, sort, display, columns: columns.layout }),
    apply: (v) => {
      setSettings({ a: v.a, b: v.b ?? null, q: v.q, filters: v.filters });
      setAxes(v.axes as SalesSummaryFilterKey[]);
      setDisplay(v.b ? (v.display ?? 'd') : 'a');
      const keys = columns.pickerColumns.map((c) => c.key);
      const k = keptSort(v.sort, v.axes.length, keys, REPORT_DEFAULT_SORT);
      setSort(k.sort);
      const c = keptColumns(v.columns, keys);
      if (c) columns.change(c.layout);
      return k.lost + (c?.lost ?? 0);
    },
    describe: (v) => describeView(v, FILTER_DIMS),
    note: t('Axes, périodes comparées, filtres, colonnes et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };
  const articleViews: StatsViewAdapter = {
    snapshot: () => ({ a: settings.a, q: settings.q, filters: settings.filters, axes: artAxes, sort: artSort, columns: artColumns.layout, receptions }),
    apply: (v) => {
      setSettings((cur) => ({ a: v.a, b: cur.b, q: v.q, filters: v.filters }));
      setArtAxes(v.axes as ArticleReportAxis[]);
      if (v.receptions) setReceptions(v.receptions);
      const keys = artColumns.pickerColumns.map((c) => c.key);
      const k = keptSort(v.sort, v.axes.length, keys, ARTICLE_DEFAULT_SORT);
      setArtSort(k.sort);
      const c = keptColumns(v.columns, keys);
      if (c) artColumns.change(c.layout);
      return k.lost + (c?.lost ?? 0);
    },
    describe: (v) => describeView(v, ARTICLE_LEVEL_DIMS),
    note: t('Axes, fenêtres des ventes et des réceptions, filtres, colonnes et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };

  const scopeLabel = view
    ? t('{articles} · {lignes}', { articles: t('{n} article', { n: view.scope.references }), lignes: t('{n} ligne', { n: view.scope.rows }) })
    : '';

  /** « On exporte ce que l'on voit » : les indicateurs de la période et de la comparaison, puis la ventilation. */
  const exportResume = () => {
    if (!view) return;
    // ⚠️ Un montant inconnu reste VIDE dans le fichier — un 0 dans un tableur devient une somme fausse.
    const money = (c: number | null) => toInputValue(c);
    const rate = csvRate;
    const kpi = (l: Translatable, x: string, y: string): string[] => [t(l, { code: currentCurrency().code }), x, y];
    exporterCsv(`resume-ventes-${view.period.from}-${view.period.to}`, [t('Indicateur'), t('Période'), t('Comparaison')], [
      kpi(key('CA net TTC ({code})'), money(view.totals.netTtcCents), money(view.refTotals.netTtcCents)),
      kpi(key('CA brut TTC ({code})'), money(view.totals.grossTtcCents), money(view.refTotals.grossTtcCents)),
      kpi(key('CA net HT ({code})'), money(view.totals.netHtCents), money(view.refTotals.netHtCents)),
      kpi(key('Marge HT ({code})'), money(view.totals.marginHtCents), money(view.refTotals.marginHtCents)),
      kpi(key('Taux de marge (%)'), rate(view.totals.marginRateBp), rate(view.refTotals.marginRateBp)),
      kpi(key('Tickets'), String(view.totals.tickets), String(view.refTotals.tickets)),
      kpi(key('Panier moyen ({code})'), money(view.totals.basketCents), money(view.refTotals.basketCents)),
      kpi(key('Pièces vendues'), csvQty(view.totals.qtySold), csvQty(view.refTotals.qtySold)),
      kpi(key('Passages'), view.totals.passages === null ? '' : String(view.totals.passages), view.refTotals.passages === null ? '' : String(view.refTotals.passages)),
      kpi(key('Transformation (%)'), rate(view.totals.conversionBp), rate(view.refTotals.conversionBp)),
      kpi(key('Taux de remise (%)'), rate(view.totals.discountRateBp), rate(view.refTotals.discountRateBp)),
      // La ventilation suit, sous une ligne vide : ses colonnes ne sont pas celles des indicateurs.
      [],
      [fieldLabel(AXES.find((x) => x.v === view.axis.key)?.field ?? 'family'), t('CA net TTC ({code})', { code: currentCurrency().code }),
        t('Part (%)'), t('Taux de marge (%)'), t('Pièces vendues'), t('Évolution (%)')],
      ...view.axis.rows.map((r) => [shownValue(r.label), money(r.netTtcCents), rate(r.shareBp), rate(r.marginRateBp), csvQty(r.qtySold), rate(r.growthBp)]),
    ]);
  };

  return (
    <>
      <div style={{ marginBottom: 0 }}>
        <h1 style={{ margin: '0 0 10px', fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Ventes')}</h1>
        <Tabs size="universe" label={t('Statistiques de ventes')} panelId="stats-ventes-panel" value={tab} onChange={pick}
          items={TABS.map((x) => ({ id: x.id, label: t(x.label), icon: x.icon }))} />
      </div>
      <div id="stats-ventes-panel" role="tabpanel">
        {tab === 'resume' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='L’essentiel d’un coup d’œil : <1>ce qui rentre, ce que ça rapporte</1>, et qui tire le résultat. Le détail vit dans Reporting ventes.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={exportResume} disabled={!view || summaryBusy} title={t('On exporte ce que l’on voit')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: view && !summaryBusy ? 'pointer' : 'default', opacity: view && !summaryBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <StatsBar value={settings} onChange={changeSettings} today={today} scope={scopeLabel} periodNote={asOfNote(view?.asOf ?? null, view?.period, view?.reference)}
              options={view?.filterOptions ?? {}} seasons={view?.seasonPeriods ?? []} />
            {!gln && (
              <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} /></div>
            )}
            {gln && status.phase === 'failed' && <ReadFailed message={status.message} />}
            {/* La PREMIÈRE mesure : le cadre du Résumé, à sa place, sans un chiffre. Les suivantes voilent les chiffres d'avant. */}
            {gln && !view && status.phase === 'loading' && <StatsResumePlaceholder />}
            {view && (
              // ⚠️ PENDANT UNE LECTURE, LES CHIFFRES D'AVANT SONT VOILÉS ET DITS : ils ne sont plus ceux des réglages affichés.
              <div aria-busy={summaryBusy} style={{ opacity: summaryBusy ? 0.55 : 1, transition: 'opacity .15s' }}>
                <StatsResume view={view} axis={axis} onAxis={setAxis} lang={currentLang()}
                  query={{ from: request.from, to: request.to, q: request.q, filters: request.filters }} />
              </div>
            )}
          </>
        ) : tab === 'reporting' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Un tableau, les axes que vous choisissez, tous les indicateurs de vente — et la <1>ligne de total</1> toujours sous les yeux.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (report && !reportBusy) exportReport(report, columns.visible, display, sort, currentLang()); }}
                disabled={!report || reportBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: report && !reportBusy ? 'pointer' : 'default', opacity: report && !reportBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="chosen"
                scope={report ? reportScope(report) : ''} periodNote={asOfNote(report?.asOf ?? null, report?.period, report?.reference)}
                options={report?.filterOptions ?? {}} seasons={report?.seasonPeriods ?? []}
                periodEnd={settings.b ? <ReportDisplayToggle value={display} onChange={setDisplay} /> : undefined}
                filtersEnd={<>
                  <ReportColumnsButton columns={columns.pickerColumns} layout={columns.layout} onChange={columns.change} />
                  <StatsViewsButton key={gln ?? ''} tab="sales-report" enabled={!!gln} adapter={reportViews} />
                </>}>
                <StatsLinesRow axes={axes} onChange={changeAxes} legend={reportLegend(columns.visible)} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} />}
              {gln && reportStatus.phase === 'failed' && <ReadFailed message={reportStatus.message} framed={false} />}
              {gln && !report && reportStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Les indicateurs sont calculés sur les pièces fiscales du magasin.')} />
              )}
              {report && (
                <StatsReportTable view={report} visible={columns.visible} display={display} sort={sort} onSort={setSort} busy={reportBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : tab === 'articles' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='La vie d’un article : ce qu’on en a <1>reçu</1>, ce qu’on en a <2>vendu</2>, ce qu’il en reste — jusqu’au gencode.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (artReport && !artBusy) exportArticles(artReport, artColumns.visible, artSort, currentLang()); }}
                disabled={!artReport || artBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: artReport && !artBusy ? 'pointer' : 'default', opacity: artReport && !artBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false}
                receptions={{ value: receptions, onChange: setReceptions, help: t('Deux fenêtres de nature différente : les quantités et montants reçus suivent la fenêtre Réceptions, tout le reste suit Ventes. « Reçu » compte toutes les entrées achetées : bons de livraison, rétrocessions entrantes et entrées reprises valorisées.') }}
                scope={artReport ? articleScope(artReport) : ''} periodNote={asOfNote(artReport?.asOf ?? null)}
                options={artReport?.filterOptions ?? {}} seasons={artReport?.seasonPeriods ?? []}
                filtersEnd={<>
                  <StatsColumnsButton columns={artColumns.pickerColumns} layout={artColumns.layout} onChange={artColumns.change} />
                  <StatsViewsButton key={gln ?? ''} tab="article-report" enabled={!!gln} adapter={articleViews} />
                </>}>
                <StatsLinesRow axes={artAxes} onChange={changeArtAxes} legend={articleLegend(artColumns.visible)} dims={ARTICLE_LEVEL_DIMS} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} />}
              {gln && artStatus.phase === 'failed' && <ReadFailed message={artStatus.message} framed={false} />}
              {gln && !artReport && artStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Les indicateurs sont calculés sur les pièces fiscales et le grand livre de stock du magasin.')} />
              )}
              {artReport && (
                <StatsArticlesTable view={artReport} visible={artColumns.visible} sort={artSort} onSort={setArtSort} busy={artBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <p style={{ flex: 1, minWidth: 260, margin: 0, fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                <Trans k='Jour après jour : <1>la cible, le réalisé, et l’an dernier en face</1> — un samedi se compare à un samedi.'
                  parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
              </p>
              <Link to="/objectifs" className="oz-stats-export" style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                whiteSpace: 'nowrap', textDecoration: 'none',
              }}>
                <Icon name="edit" size={14} stroke={1.9} />{t('Modifier les objectifs')}
              </Link>
              <button type="button" onClick={() => { if (objView && !objBusy) exportObjectives(objView, objColumns.visible); }}
                disabled={!objView || objBusy} title={t('On exporte ce que l’on voit')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: objView && !objBusy ? 'pointer' : 'default', opacity: objView && !objBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.9} />{t('Export Excel')}
              </button>
            </div>
            <StatsPeriodCard value={objWindow} onChange={setObjWindow} today={today}
              note={objView?.capped ? t('Suivi limité à 2 mois : au-delà, la lecture jour par jour n’a plus de sens.') : undefined} />
            {!gln && (
              <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} /></div>
            )}
            {gln && objStatus.phase === 'failed' && <ReadFailed message={objStatus.message} />}
            {gln && !objView && objStatus.phase === 'loading' && (
              <div className="dcard"><EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Les objectifs sont répartis sur leurs jours et confrontés aux pièces fiscales du magasin.')} /></div>
            )}
            {objView && (
              <StatsObjectives view={objView} visible={objColumns.visible} busy={objBusy} lang={currentLang()}
                columnsButton={<StatsColumnsButton columns={objColumns.pickerColumns} layout={objColumns.layout} onChange={objColumns.change} />} />
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * Une lecture qui n'a pas abouti — avec son motif réel. Ce n'est pas une période sans vente, c'est une mesure qui n'a pas
 * pu être faite : aucun chiffre ne s'affiche.
 * ⚠️ `text` : la phrase entière d'un autre univers (le stock) — jamais un morceau glissé dans celle-ci.
 */
export function ReadFailed({ message, framed = true, text }: { message: string; framed?: boolean; text?: string }) {
  return (
    <div className={framed ? 'dcard' : undefined} role="alert" style={{ padding: 24 }}>
      <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>{t('Statistiques illisibles')}</div>
      <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {text ?? t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas une période sans vente, c’est une mesure qui n’a pas pu être faite.', { message })}
      </p>
    </div>
  );
}

/**
 * « 412 articles · 18 lignes », ou « … · 25 combinaisons » quand il y a plusieurs niveaux — ceux de la réponse.
 * ⚠️ Partagé par les reportings des ventes et du stock.
 */
export function reportScope(view: Pick<SalesReportView, 'axes' | 'scope'>): string {
  const articles = t('{n} article', { n: view.scope.references });
  const rows = view.axes.length > 1 ? t('{n} combinaison', { n: view.scope.rows }) : t('{n} ligne', { n: view.scope.rows });
  return t('{articles} · {lignes}', { articles, lignes: rows });
}

/** Deux bornes `AAAAMMJJ`. */
interface DaySpan { from: string; to: string }

/** Le nombre de jours d'une fenêtre, bornes comprises — en UTC, pour qu'un changement d'heure ne retire pas un jour. */
const spanDays = (w: DaySpan): number => {
  const at = (ymd: string) => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));
  return Math.round((at(w.to) - at(w.from)) / 86_400_000) + 1;
};

/**
 * LA NOTE « À DATE » — la période finissait dans le futur (une saison en cours) : le serveur l'a arrêtée à aujourd'hui.
 * Le dire évite de lire un écart de saison complète.
 * ⚠️ « LA COMPARAISON COUVRE LA MÊME DURÉE » SE CONSTATE, elle ne se suppose pas : sans comparaison il n'y a rien à
 *    couvrir, et une comparaison choisie plus courte que la durée écoulée n'est jamais allongée par le serveur — la
 *    phrase ne se dit que quand les deux fenêtres rendues ont le même nombre de jours.
 */
export function asOfNote(asOf: string | null, period?: DaySpan, reference?: DaySpan | null): string | undefined {
  if (!asOf) return undefined;
  const day = new Date(Number(asOf.slice(0, 4)), Number(asOf.slice(4, 6)) - 1, Number(asOf.slice(6, 8)));
  const date = new Intl.DateTimeFormat(currentLang(), { dateStyle: 'long' }).format(day);
  const sameSpan = !!period && !!reference && spanDays(period) === spanDays(reference);
  return sameSpan ? t('À date : arrêtée au {date}, et la comparaison couvre la même durée.', { date }) : t('À date : arrêtée au {date}.', { date });
}

/** La fenêtre Réceptions par défaut : du premier jour du mois, trois mois plus tôt, jusqu'à aujourd'hui (maquette). */
function defaultReceptions(today: Date): StatsWindow {
  return { mode: 'dates', from: isoOf(new Date(today.getFullYear(), today.getMonth() - 3, 1)), to: isoOf(today) };
}
